import { Container, Geometry, Mesh, Shader, type UniformGroup } from 'pixi.js';
import { VISUALS } from '../config.ts';
import type { Ball } from '../simulation/Ball.ts';

/**
 * The ball, as a shaded sphere.
 *
 * Drawn with a shader rather than stacked Graphics circles: seen from directly above, a
 * sphere is almost entirely specular highlight and rim falloff, and neither survives
 * being approximated with a handful of flat discs. One quad, one draw call, a real
 * normal at every pixel.
 *
 * It renders above the fluid on purpose. A ball that vanishes the moment it is engulfed
 * makes the level unreadable, and reading the ball's position is the whole game.
 */

/** Quad half-size as a multiple of the ball radius — the slack holds the shadow. */
const QUAD_PAD = 1.7;

const vertex = /* glsl */ `#version 300 es
in vec2 aPosition;

uniform mat3 uProjectionMatrix;
uniform mat3 uWorldTransformMatrix;
uniform mat3 uTransformMatrix;

out vec2 vLocal;

void main() {
    mat3 mvp = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;
    gl_Position = vec4((mvp * vec3(aPosition, 1.0)).xy, 0.0, 1.0);
    vLocal = aPosition;
}
`;

const fragment = /* glsl */ `#version 300 es
precision highp float;

in vec2 vLocal;
out vec4 fragColor;

uniform vec2  uLightDirection;
uniform vec2  uShadowOffset;
uniform float uCoating;
uniform float uSpin;

const vec3 STEEL_DARK = vec3(0.28, 0.31, 0.38);
const vec3 STEEL_LIT  = vec3(0.93, 0.95, 1.0);
const vec3 COATED     = vec3(0.05, 0.055, 0.075);

void main() {
    // Soft contact shadow, cast away from the light.
    float shadowDistance = length(vLocal - uShadowOffset);
    float shadow = (1.0 - smoothstep(0.72, 1.12, shadowDistance)) * 0.5;

    float radius = length(vLocal);
    if (radius >= 1.0) {
        if (shadow <= 0.003) discard;
        fragColor = vec4(0.0, 0.0, 0.0, shadow);
        return;
    }

    // Reconstruct the hemisphere facing the camera.
    vec3 normal = vec3(vLocal, sqrt(max(1.0 - radius * radius, 0.0)));
    vec3 view = vec3(0.0, 0.0, 1.0);
    vec3 light = normalize(vec3(uLightDirection, 0.62));
    vec3 halfway = normalize(light + view);

    float diffuse = max(dot(normal, light), 0.0);
    float specular = pow(max(dot(normal, halfway), 0.0), 140.0);
    float sheen = pow(max(dot(normal, halfway), 0.0), 16.0);
    float fresnel = pow(1.0 - normal.z, 3.0);

    // Ferrofluid coating darkens the ball and makes it glossier, previewing the coating
    // mechanic without enabling it.
    vec3 base = mix(STEEL_DARK, COATED, uCoating);
    vec3 color = base * (0.22 + diffuse * 0.85);
    color += STEEL_LIT * specular * (1.1 + uCoating * 0.6);
    color += STEEL_LIT * sheen * 0.14;
    color += vec3(0.32, 0.40, 0.58) * fresnel * (0.35 + uCoating * 0.4);

    // A faint meridian so the cosmetic roll is visible at all.
    float meridian = abs(sin(atan(vLocal.y, vLocal.x) * 2.0 + uSpin));
    color *= 1.0 - 0.06 * (1.0 - meridian) * normal.z;

    // Antialias the silhouette, then premultiply for Pixi's normal blend.
    float edge = 1.0 - smoothstep(0.965, 1.0, radius);
    float alpha = edge;
    fragColor = vec4(color * alpha, alpha + shadow * (1.0 - alpha));
}
`;

export class BallRenderer {
  readonly container = new Container({ label: 'ball' });

  private readonly mesh: Mesh<Geometry, Shader>;
  private readonly uniforms: UniformGroup;

  constructor() {
    const geometry = new Geometry({
      attributes: {
        aPosition: new Float32Array([
          -QUAD_PAD, -QUAD_PAD, QUAD_PAD, -QUAD_PAD, QUAD_PAD, QUAD_PAD, -QUAD_PAD, QUAD_PAD,
        ]),
      },
      indexBuffer: new Uint32Array([0, 1, 2, 0, 2, 3]),
    });

    const shader = Shader.from({
      gl: { vertex, fragment },
      resources: {
        ballUniforms: {
          uLightDirection: { value: new Float32Array([0, 0]), type: 'vec2<f32>' },
          uShadowOffset: { value: new Float32Array([0, 0]), type: 'vec2<f32>' },
          uCoating: { value: 0, type: 'f32' },
          uSpin: { value: 0, type: 'f32' },
        },
      },
    });

    this.uniforms = shader.resources.ballUniforms as UniformGroup;
    this.mesh = new Mesh({ geometry, shader, label: 'ball' });
    this.container.addChild(this.mesh);
  }

  /** `alpha` interpolates between the tick's start and end positions. */
  update(ball: Ball | null, alpha: number): void {
    // Fluid-objective levels have no ball at all.
    this.container.visible = ball !== null;
    if (!ball) return;

    this.mesh.position.set(
      ball.previousPosition.x + (ball.position.x - ball.previousPosition.x) * alpha,
      ball.previousPosition.y + (ball.position.y - ball.previousPosition.y) * alpha,
    );
    // Geometry is a unit sphere, so world size is entirely in the transform.
    this.mesh.scale.set(ball.radius);

    const uniforms = this.uniforms.uniforms;
    const light = uniforms.uLightDirection as Float32Array;
    light[0] = VISUALS.lightDirection.x;
    light[1] = VISUALS.lightDirection.y;

    const shadow = uniforms.uShadowOffset as Float32Array;
    shadow[0] = (-VISUALS.lightDirection.x * VISUALS.shadowOffset) / ball.radius;
    shadow[1] = (-VISUALS.lightDirection.y * VISUALS.shadowOffset) / ball.radius;

    uniforms.uCoating = Math.min(1, ball.fluidCoverage * 1.2);
    uniforms.uSpin = ball.spin;
  }
}
